import {isNumber} from '@gl/lit/utils/index.js';
import {pick} from '@gl/lit/utils/objects.js';

export type Weekday = WeekdaysEnum;

export type WeekdayDescription = {
  value: Weekday;
  longName: string;
  shortName: string;
  narrowName: string;
};

export enum WeekdaysEnum {
  SUNDAY,
  MONDAY,
  TUESDAY,
  WEDNESDAY,
  THURSDAY,
  FRIDAY,
  SATURDAY,
}

export const WEEKEND_DAYS_PER_LOCALE: Record<string, WeekdaysEnum[]> = {
  en: [WeekdaysEnum.SATURDAY, WeekdaysEnum.SUNDAY],
};

export const WORK_DAYS_PER_LOCALE: Record<string, WeekdaysEnum[]> = {
  en: [
    WeekdaysEnum.MONDAY,
    WeekdaysEnum.TUESDAY,
    WeekdaysEnum.WEDNESDAY,
    WeekdaysEnum.THURSDAY,
    WeekdaysEnum.FRIDAY,
  ],
};

export function setWeekday(value: unknown): Weekday {
  if (!isWeekday(value)) throw new Error(`${value} is an invalid weekday`);
  return value;
}

export function isWeekday(value: unknown): value is Weekday {
  const numericValue = Number(value);
  return (
    isNumber(numericValue) &&
    !Number.isNaN(numericValue) &&
    numericValue >= WeekdaysEnum.SUNDAY &&
    numericValue <= WeekdaysEnum.SATURDAY
  );
}

export function getWeekdaysList(formatter: Intl.DateTimeFormat) {
  /**
   * 2023 started on a Sunday so we use it to get the weekdays
   * starting on the Sunday and ending on the Saturday
   */
  return [...Array(7).keys()].map(day =>
    formatter.format(new Date(2023, 0, day + 1, 12, 0))
  );
}

export function getWeekdays(
  locale = 'en'
): Record<WeekdaysEnum, WeekdayDescription> {
  const longFormatter = new Intl.DateTimeFormat(locale, {weekday: 'long'});
  const shortFormatter = new Intl.DateTimeFormat(locale, {weekday: 'short'});
  const narrowFormatter = new Intl.DateTimeFormat(locale, {weekday: 'narrow'});
  const longList = getWeekdaysList(longFormatter);
  const shortList = getWeekdaysList(shortFormatter);
  const narrowList = getWeekdaysList(narrowFormatter);

  return {
    [WeekdaysEnum.SUNDAY]: {
      longName: longList[WeekdaysEnum.SUNDAY],
      shortName: shortList[WeekdaysEnum.SUNDAY],
      narrowName: narrowList[WeekdaysEnum.SUNDAY],
      value: setWeekday(WeekdaysEnum.SUNDAY),
    },
    [WeekdaysEnum.MONDAY]: {
      longName: longList[WeekdaysEnum.MONDAY],
      shortName: shortList[WeekdaysEnum.MONDAY],
      narrowName: narrowList[WeekdaysEnum.MONDAY],
      value: setWeekday(WeekdaysEnum.MONDAY),
    },
    [WeekdaysEnum.TUESDAY]: {
      longName: longList[WeekdaysEnum.TUESDAY],
      shortName: shortList[WeekdaysEnum.TUESDAY],
      narrowName: narrowList[WeekdaysEnum.TUESDAY],
      value: setWeekday(WeekdaysEnum.TUESDAY),
    },
    [WeekdaysEnum.WEDNESDAY]: {
      longName: longList[WeekdaysEnum.WEDNESDAY],
      shortName: shortList[WeekdaysEnum.WEDNESDAY],
      narrowName: narrowList[WeekdaysEnum.WEDNESDAY],
      value: setWeekday(WeekdaysEnum.WEDNESDAY),
    },
    [WeekdaysEnum.THURSDAY]: {
      longName: longList[WeekdaysEnum.THURSDAY],
      shortName: shortList[WeekdaysEnum.THURSDAY],
      narrowName: narrowList[WeekdaysEnum.THURSDAY],
      value: setWeekday(WeekdaysEnum.THURSDAY),
    },
    [WeekdaysEnum.FRIDAY]: {
      longName: longList[WeekdaysEnum.FRIDAY],
      shortName: shortList[WeekdaysEnum.FRIDAY],
      narrowName: narrowList[WeekdaysEnum.FRIDAY],
      value: setWeekday(WeekdaysEnum.FRIDAY),
    },
    [WeekdaysEnum.SATURDAY]: {
      longName: longList[WeekdaysEnum.SATURDAY],
      shortName: shortList[WeekdaysEnum.SATURDAY],
      narrowName: narrowList[WeekdaysEnum.SATURDAY],
      value: setWeekday(WeekdaysEnum.SATURDAY),
    },
  };
}

export const getWeekendDays = (locale = 'en') => {
  return pick(
    WEEKEND_DAYS_PER_LOCALE[locale] ?? WEEKEND_DAYS_PER_LOCALE['en'],
    getWeekdays(locale)
  );
};

export const getWorkDays = (locale = 'en') => {
  return pick(
    WORK_DAYS_PER_LOCALE[locale] ?? WORK_DAYS_PER_LOCALE['en'],
    getWeekdays(locale)
  );
};
